/**
 * Footer コンポーネント
 *
 * グローバルフッター。ライセンス表記とプロジェクト情報を表示。
 *
 */

import { useI18n } from '@/i18n/useI18n';
import agencyLogo from '@/assets/agency-for-cultural-affairs-logo.png';

/**
 * Footer コンポーネント
 *
 * 使用例:
 * ```tsx
 * <Footer />
 * ```
 */
export function Footer() {
  const { t } = useI18n();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full border-t bg-background">
      <div className="container mx-auto px-4 py-6">
        <div className="grid gap-6 md:grid-cols-3">
          {/* プロジェクト情報 */}
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('footer.projectTitle')}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t('footer.projectDescription')}
            </p>
          </div>

          {/* ライセンス表記 */}
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('footer.licenseTitle')}
            </h3>
            <div className="space-y-2 text-sm text-muted-foreground">
              <div>
                <span className="font-medium text-foreground">
                  {t('footer.metadataLicense')}:{' '}
                </span>
                <a
                  href="https://creativecommons.org/licenses/by/4.0/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  CC BY 4.0
                </a>
              </div>
              <div>
                <span className="font-medium text-foreground">
                  {t('footer.thumbnailLicense')}:{' '}
                </span>
                <span>All Rights Reserved</span>
              </div>
            </div>
          </div>

          {/* リンク */}
          <div>
            <h3 className="mb-2 text-sm font-semibold">
              {t('footer.linksTitle')}
            </h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>
                <a
                  href="https://archiveanimation.wordpress.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {t('footer.originalArchiveLink')}
                </a>
              </li>
              <li>
                <a href="/about" className="hover:underline">
                  {t('footer.aboutLink')}
                </a>
              </li>
              <li>
                <a href="/metadata" className="hover:underline">
                  {t('footer.metadataLink')}
                </a>
              </li>
              <li>
                <a
                  href="https://forms.gle/7mkpzZtXt56UHzx46"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {t('footer.contactLink')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* 助成金クレジット */}
        <div className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
          <img
            src={agencyLogo}
            alt={t('footer.grantLogoAlt')}
            className="h-12 w-auto"
            width="120"
            height="48"
            loading="lazy"
          />
          <p className="text-sm text-muted-foreground">
            {t('footer.grantText')}
          </p>
        </div>

        {/* コピーライト */}
        <div className="mt-6 border-t pt-6 text-center text-sm text-muted-foreground">
          {t('footer.rights', { year: currentYear })}
        </div>
      </div>
    </footer>
  );
}
